import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { cn } from "../../lib/cn";

export const PAGE_SIZES = [10, 25, 50, 100] as const;

/**
 * Two pagination shapes, because the API genuinely has two.
 *
 *  - "counted": the client holds the complete result set (endpoints that return a plain array),
 *    so total, page count and jump-to-last are all real numbers.
 *  - "cursor": `/messages` and `/contacts` return `{ items, nextCursor, hasMore }` and **no
 *    total**. There is no count query behind them, so the number of pages is unknowable without
 *    changing the backend — which this task forbids.
 *
 * The cursor variant therefore renders "Showing 1–25" with no "of Z", a page number with no
 * "/ N", and a disabled last-page button carrying a title that says why. Inventing a total would
 * mean printing a number that is not true.
 */
export type PaginationProps =
  | {
      mode: "counted";
      page: number;
      pageSize: number;
      total: number;
      onPageChange: (page: number) => void;
      onPageSizeChange: (size: number) => void;
      className?: string;
    }
  | {
      mode: "cursor";
      page: number;
      pageSize: number;
      /** Rows on the current page, for the range text. */
      itemCount: number;
      hasMore: boolean;
      /**
       * Total matching rows, when the endpoint reports one. With it the bar can show a page
       * count and offer jump-to-last; without it both degrade gracefully to the old behaviour.
       */
      total?: number;
      onNext: () => void;
      onPrev: () => void;
      onFirst: () => void;
      /** Jump to the final page. Omit when the endpoint cannot serve one. */
      onLast?: () => void;
      onPageSizeChange: (size: number) => void;
      className?: string;
    };

function NavButton({
  onClick,
  disabled,
  label,
  title,
  children,
}: {
  onClick: () => void;
  disabled: boolean;
  label: string;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={title ?? label}
      className={cn(
        "inline-flex size-8 items-center justify-center rounded-control text-text-muted",
        "transition-colors duration-fast ease-standard",
        "hover:bg-surface-hover hover:text-text active:bg-surface-hover",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface",
        "disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-text-muted"
      )}
    >
      {children}
    </button>
  );
}

function PageSize({
  value,
  onChange,
}: {
  value: number;
  onChange: (size: number) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-xs text-text-muted">
      Rows
      {/*
        The chevron is masked onto its OWN span, not onto the select.

        It used to be a mask on the <select> itself, and a mask clips the WHOLE element to its
        shape — so the border, the background and the selected number were all masked away and
        the control rendered as a bare chevron with no visible value. That is the bug this fixes:
        the user could not see whether they were showing 10 rows or 100.

        The mask still beats a background-image because the chevron's colour has to come from a
        token: a data: URI is a separate document and cannot read a CSS variable, so a baked-in
        stroke stayed dark-theme grey and went near-invisible on the light surface. Under a mask
        the SVG supplies only the shape and backgroundColor supplies the colour, which themes.
      */}
      <span className="relative inline-flex items-center">
        <select
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          aria-label="Rows per page"
          className={cn(
            "h-8 rounded-control border border-border bg-surface pl-2 pr-7 text-xs text-text",
            "appearance-none",
            "transition-colors duration-fast ease-standard",
            "focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
          )}
        >
          {PAGE_SIZES.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
        <span
          aria-hidden
          // pointer-events-none so the chevron never swallows a click meant for the select.
          className="pointer-events-none absolute right-2 size-3"
          style={{
            backgroundColor: "var(--sb-text-muted)",
            maskImage:
              "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23000' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
            WebkitMaskImage:
              "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23000' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
            maskRepeat: "no-repeat",
            WebkitMaskRepeat: "no-repeat",
            maskPosition: "center",
            WebkitMaskPosition: "center",
            maskSize: "contain",
            WebkitMaskSize: "contain",
          }}
        />
      </span>
    </label>
  );
}

/**
 * Sticky to the bottom of the content area so it stays reachable while a long table scrolls.
 * `sticky` rather than a nested scroll container: nesting a scrollable region inside the page
 * scroll is awkward on touch — the inner region swallows the gesture and the page cannot be
 * scrolled from over the table — and at 360px it would leave a very small window to read rows in.
 */
export function Pagination(props: PaginationProps) {
  const shell = cn(
    "sticky bottom-0 z-20 flex flex-wrap items-center justify-between gap-x-4 gap-y-2",
    // Opaque for the same reason as the header: rows scrolling beneath must not show through
    // the range text or the page number.
    "rounded-card border border-border bg-surface px-3 py-2",
    props.className
  );

  if (props.mode === "counted") {
    const { page, pageSize, total, onPageChange, onPageSizeChange } = props;
    const pageCount = Math.max(1, Math.ceil(total / pageSize));
    const current = Math.min(page, pageCount);
    const first = total === 0 ? 0 : (current - 1) * pageSize + 1;
    const last = Math.min(current * pageSize, total);

    return (
      <div className={shell}>
        <p className="tnum text-xs text-text-muted">
          {total === 0 ? "No results" : `Showing ${first}–${last} of ${total}`}
        </p>
        <div className="flex items-center gap-3">
          <PageSize value={pageSize} onChange={onPageSizeChange} />
          <div className="flex items-center gap-1">
            <NavButton
              onClick={() => onPageChange(1)}
              disabled={current <= 1}
              label="First page"
            >
              <ChevronsLeft className="size-4" aria-hidden />
            </NavButton>
            <NavButton
              onClick={() => onPageChange(current - 1)}
              disabled={current <= 1}
              label="Previous page"
            >
              <ChevronLeft className="size-4" aria-hidden />
            </NavButton>
            <span className="tnum min-w-[4.5rem] text-center text-xs text-text-muted">
              {current} / {pageCount}
            </span>
            <NavButton
              onClick={() => onPageChange(current + 1)}
              disabled={current >= pageCount}
              label="Next page"
            >
              <ChevronRight className="size-4" aria-hidden />
            </NavButton>
            <NavButton
              onClick={() => onPageChange(pageCount)}
              disabled={current >= pageCount}
              label="Last page"
            >
              <ChevronsRight className="size-4" aria-hidden />
            </NavButton>
          </div>
        </div>
      </div>
    );
  }

  const {
    page,
    pageSize,
    itemCount,
    hasMore,
    total,
    onNext,
    onPrev,
    onFirst,
    onLast,
    onPageSizeChange,
  } = props;
  const first = itemCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = (page - 1) * pageSize + itemCount;
  // Known only when the endpoint reports a total; undefined keeps the old count-less display.
  const pageCount =
    total === undefined ? undefined : Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className={shell}>
      <p className="tnum text-xs text-text-muted">
        {itemCount === 0
          ? "No results"
          : total === undefined
          ? `Showing ${first}–${last}`
          : `Showing ${first}–${last} of ${total}`}
      </p>
      <div className="flex items-center gap-3">
        <PageSize value={pageSize} onChange={onPageSizeChange} />
        <div className="flex items-center gap-1">
          <NavButton onClick={onFirst} disabled={page <= 1} label="First page">
            <ChevronsLeft className="size-4" aria-hidden />
          </NavButton>
          <NavButton
            onClick={onPrev}
            disabled={page <= 1}
            label="Previous page"
          >
            <ChevronLeft className="size-4" aria-hidden />
          </NavButton>
          <span className="tnum min-w-[4.5rem] text-center text-xs text-text-muted">
            {pageCount === undefined
              ? `Page ${page}`
              : `${page} / ${pageCount}`}
          </span>
          <NavButton onClick={onNext} disabled={!hasMore} label="Next page">
            <ChevronRight className="size-4" aria-hidden />
          </NavButton>
          {/*
            Jump-to-last works on a keyset list because the server flips the sort rather than
            offsetting: the last page descending is the first page ascending. It is only offered
            when the endpoint reports a total, since without one there is no way to know which
            page number the user has landed on.
          */}
          <NavButton
            onClick={() => onLast?.()}
            disabled={!onLast || pageCount === undefined || page >= pageCount}
            label="Last page"
          >
            <ChevronsRight className="size-4" aria-hidden />
          </NavButton>
        </div>
      </div>
    </div>
  );
}
